<template>
  <div v-if="isLoading" class="flex flex-col justify-center items-center py-20 space-y-4">
    <h1 class="text-xl font-heading">Confirming order...</h1>
    <div class="w-full max-w-sm bg-gray-200 rounded-full h-2 overflow-hidden">
      <div class="bg-primary h-2 rounded-full w-1/4 animate-indeterminate-progress"></div>
    </div>
  </div>

  <div v-else-if="reservationFailed" class="px-4 max-w-[600px] mx-auto py-20">
    <h1 class="text-3xl font-bold font-heading mb-2">Reservation Unsuccessful</h1>
    <p class="mb-2">We're sorry—the table you selected was just reserved by another guest.</p>
    <p class="mb-2 font-semibold">Your payment was not processed.</p>
    <p class="mb-8">Please try again to choose another available table.</p>
    <button
      @click="handleSelectTables"
      class="px-4 py-2 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
      id="select-tables"
    >
      Select Tables
    </button>
  </div>

  <div v-else-if="initialLoadError" class="px-4 max-w-[600px] mx-auto py-20">
    <h1 class="text-3xl font-bold font-heading mb-4">Something went wrong.</h1>
    <p class="mb-8">We couldn't load your order details. Please try again.</p>
    <button
      class="px-4 py-2 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
      @click="handleInitialLoadRetry"
    >
      Retry
    </button>
  </div>

  <div v-else-if="timeoutError" class="px-4 max-w-[600px] mx-auto py-20">
    <h1 class="text-3xl font-bold font-heading mb-4">Confirmation Timed Out</h1>
    <p class="mb-8">We're having trouble confirming your reservation. Please try again.</p>
    <button
      class="px-4 py-2 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
      @click="startConnection"
    >
      Retry
    </button>
  </div>

  <div v-else-if="webSocketError" class="px-4 max-w-[600px] mx-auto py-20">
    <h1 class="text-3xl font-bold font-heading mb-4">Connection Error</h1>
    <p class="mb-8">
      We couldn't connect to our server to confirm your reservation. Please check your internet
      connection and try again.
    </p>
    <button
      class="px-4 py-2 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
      @click="startConnection"
    >
      Retry
    </button>
  </div>
  <section v-else class="px-4 max-w-screen-xl mx-auto pb-10 pt-10">
    <slot></slot>
  </section>
</template>

<script setup lang="ts">
import useOrderConfirmationStore from '../../stores/OrderConfirmationStore'
import { useWebSocket } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { useCheckoutStore } from '@/stores/checkout'
import { blankError } from '@/utils/errors'

import { ReservationStatus, ReservationStatusType } from '@generated/types'

const props = defineProps<{
  reservationId: string
  reservationKey: string
}>()

const isLoading = ref<boolean>(true)
const reservationFailed = ref<boolean>(false)
const initialLoadError = ref<boolean>(false)
const timeoutError = ref<boolean>(false)
const webSocketError = ref<boolean>(false)
const confirmationTimeout = ref<number | undefined>()

const orderConfirmationStore = useOrderConfirmationStore()
const { performance, reservation } = storeToRefs(orderConfirmationStore)

onMounted(loadOrderDetails)

async function loadOrderDetails() {
  try {
    await orderConfirmationStore.loadOrderDetails(props.reservationId, props.reservationKey)
    if (!reservation.value) {
      timeoutError.value = true
    } else if (reservation.value.status === 'authorized') {
      startConnection()
    } else {
      isLoading.value = false
    }
  } catch (e) {
    initialLoadError.value = true
    isLoading.value = false
  }
}

const host = window.location.host
const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
const wsUrl = `${protocol}://${host}/ws/guest/${props.reservationId}/?reservation_key=${props.reservationKey}`
const { open, close } = useWebSocket(wsUrl, {
  autoClose: false,
  onMessage(_, event) {
    if (confirmationTimeout.value) clearTimeout(confirmationTimeout.value)
    const data = JSON.parse(event.data)

    if (data.type === 'reservation_status_change') {
      const newStatus = data.message.status as ReservationStatusType
      if (newStatus === ReservationStatus.FAILED) {
        reservationFailed.value = true
      }
      isLoading.value = false
      close()
    }
  },
  onError() {
    if (confirmationTimeout.value) clearTimeout(confirmationTimeout.value)
    webSocketError.value = true
    isLoading.value = false
    close()
  },
})
function startConnection() {
  isLoading.value = true
  initialLoadError.value = false
  timeoutError.value = false
  webSocketError.value = false
  reservationFailed.value = false
  open()
  confirmationTimeout.value = window.setTimeout(() => {
    timeoutError.value = true
    isLoading.value = false
    close()
  }, 30000)
}

const router = useRouter()
const checkoutError = blankError()
const checkoutStore = useCheckoutStore()
async function handleSelectTables() {
  if (!performance.value || !reservation.value) {
    await router.replace({ name: 'setGuests' })
    return
  }

  await checkoutStore.setPerformance(performance.value!, checkoutError)
  await checkoutStore.setNumberOfGuests(reservation.value!.number_of_guests, checkoutError)
  await router.push({ name: 'setTable' })
}

function handleInitialLoadRetry() {
  isLoading.value = true
  initialLoadError.value = false
  loadOrderDetails()
}

onUnmounted(() => {
  if (confirmationTimeout.value) {
    clearTimeout(confirmationTimeout.value)
  }
  close()
})
</script>

<style>
@keyframes indeterminate-progress {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(400%);
  }
}

.animate-indeterminate-progress {
  animation: indeterminate-progress 1.5s linear infinite;
}
</style>
